/**
 * Scene Editor shell — PlayEngine-style vertical split:
 * hierarchy (outliner) on top, properties inspector below.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import _ from "lodash";
import SceneEditorHierarchy from "./containerSceneEditorHierarchy";
import SceneEditorInspector from "./containerSceneEditorInspector";
import { SceneEditorLanguageBar, SceneEditorPlayableSize } from "./sceneEditorVersionMeta";

const PANEL_BG = "#2b2b2e";

function SplitHandle({ onPointerDown }) {
  return (
    <div
      onPointerDown={onPointerDown}
      style={{
        flex: "0 0 auto",
        height: 6,
        cursor: "row-resize",
        backgroundColor: "#2b2b2b",
        borderTop: "1px solid #1f1f1f",
        borderBottom: "1px solid #1f1f1f",
      }}
    />
  );
}

export default function containerSceneEditor(props) {
  const isLocalization = props.mode === "localization";
  const splitStorageKey = isLocalization
    ? "sceneEditorLocalizationSplit"
    : "sceneEditorVersionSettingsSplit";
  const containerRef = useRef(null);
  const hierarchyPaneRef = useRef(null);
  const inspectorPaneRef = useRef(null);
  const [availHeight, setAvailHeight] = useState(0);
  const [treeRatio, setTreeRatio] = useState(() => {
    const raw = window.localStorage.getItem(splitStorageKey);
    const n = raw != null ? Number(raw) : 0.45;
    return Number.isFinite(n) ? Math.max(0.2, Math.min(0.8, n)) : 0.45;
  });
  const [selectedComponentIds, setSelectedComponentIds] = useState([]);

  useEffect(() => {
    setSelectedComponentIds([]);
  }, [_.get(props, "selectedBase._id")]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    function findScrollParent(node) {
      let p = node.parentElement;
      while (p) {
        const oy = window.getComputedStyle(p).overflowY;
        if (oy === "auto" || oy === "scroll") return p;
        p = p.parentElement;
      }
      return null;
    }
    const sp = findScrollParent(el);
    function measure() {
      const rect = el.getBoundingClientRect();
      if (rect.top === 0 && rect.height === 0) return;
      const bottom = sp
        ? sp.getBoundingClientRect().top + sp.clientHeight
        : window.innerHeight - 8;
      const next = Math.max(240, Math.floor(bottom - rect.top));
      setAvailHeight((prev) => (prev === next ? prev : next));
    }
    measure();
    const raf1 = requestAnimationFrame(measure);
    const raf2 = requestAnimationFrame(() => requestAnimationFrame(measure));
    window.addEventListener("resize", measure);
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      if (sp) ro.observe(sp);
      if (el.parentElement) ro.observe(el.parentElement);
    }
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
    };
  }, []);

  function startDividerDrag(e) {
    e.preventDefault();
    const hierarchyPane = hierarchyPaneRef.current;
    const inspectorPane = inspectorPaneRef.current;
    if (!hierarchyPane || !inspectorPane) return;
    const startY = e.clientY;
    const startRatio = treeRatio;
    const splitHeight =
      hierarchyPane.getBoundingClientRect().height +
      inspectorPane.getBoundingClientRect().height;
    if (splitHeight <= 0) return;
    const prevUserSelect = document.body.style.userSelect;
    const prevCursor = document.body.style.cursor;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "row-resize";
    hierarchyPane.style.pointerEvents = "none";
    inspectorPane.style.pointerEvents = "none";
    function onMove(ev) {
      const next = Math.max(0.2, Math.min(0.8, startRatio + (ev.clientY - startY) / splitHeight));
      setTreeRatio(next);
    }
    function onUp() {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.body.style.userSelect = prevUserSelect;
      document.body.style.cursor = prevCursor;
      hierarchyPane.style.pointerEvents = "";
      inspectorPane.style.pointerEvents = "";
      setTreeRatio((r) => {
        window.localStorage.setItem(splitStorageKey, String(r));
        return r;
      });
    }
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  }

  return (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        flexDirection: "column",
        height: availHeight ? `${availHeight}px` : "80vh",
        minHeight: 0,
        overflow: "hidden",
        backgroundColor: PANEL_BG,
      }}
    >
      <div style={{ flex: "0 0 auto" }}>
        {isLocalization ? (
          <SceneEditorLanguageBar
            selectedLanguage={props.selectedLanguage}
            supportedLanguages={_.get(props, "selectedBase.supportedLanguages", {})}
            createdLanguages={_.get(props, "selectedVersion.createdLanguages")}
            disabled={props.isVersionLocked}
            onLanguageChange={props.onLanguageChange}
            onRemoveLanguage={props.onRemoveLanguage}
          />
        ) : (
          <SceneEditorPlayableSize
            selectedVersion={props.selectedVersion}
            selectedBase={props.selectedBase}
            setTotalSize={props.setTotalSize}
            assetEnabledByComponent={_.get(props, "selectedModifiedBase.assetEnabledByComponent", {})}
            selectedLanguage={props.selectedLanguage}
          />
        )}
      </div>
      {props.localizationHeader ? (
        <div style={{ flex: "0 0 auto" }}>{props.localizationHeader}</div>
      ) : null}

      <div
        ref={hierarchyPaneRef}
        style={{
          flexGrow: treeRatio,
          flexShrink: 1,
          flexBasis: 0,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <SceneEditorHierarchy
          selectedBase={props.selectedBase}
          selectedVersion={props.selectedVersion}
          setSelectedVersionPathValue={props.setSelectedVersionPathValue}
          persistVersionPathValue={props.persistVersionPathValue}
          predefinedGameComponents={props.predefinedGameComponents}
          userPermissions={props.userPermissions}
          createSnackbar={props.createSnackbar}
          selectedItems={selectedComponentIds}
          onSelectedItemsChange={setSelectedComponentIds}
          defaultVersion={props.defaultVersion}
          versions={props.versions}
          selectedModifiedBase={props.selectedModifiedBase}
          hierarchyReadOnly={isLocalization}
          settingsSource={isLocalization ? "localization" : "version"}
        />
      </div>

      <SplitHandle onPointerDown={startDividerDrag} />

      <div
        ref={inspectorPaneRef}
        style={{
          flexGrow: 1 - treeRatio,
          flexShrink: 1,
          flexBasis: 0,
          minHeight: 0,
          overflowY: "auto",
          backgroundColor: PANEL_BG,
        }}
      >
        <SceneEditorInspector
          {...props}
          selectedComponentIds={selectedComponentIds}
          settingsSource={isLocalization ? "localization" : "version"}
        />
      </div>
    </div>
  );
}
